import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSalesforce } from '../hooks/useSalesforce';
import Navbar from '../components/Navbar';
import { supabase } from '../services/supabaseClient';
import Chatbot from '../components/Chatbot';
import OrgSwitcher from '../components/OrgSwitcher';

// Utility function to clean up object names
const cleanObjectName = (objectName: string): string => {
  return objectName
    .replace(/__c$/, '') // Remove __c suffix
    .replace(/__/g, ' ')  // Replace __ with space
    .replace(/_/g, ' ');  // Replace _ with space
};

interface SalesforceObject {
  id: string;
  flow_id: string;
  object_name: string;
  field_name: string;
  relationship_type: string;
  reference_type?: string;
  created_at: string;
  updated_at: string;
  flow?: {
    id: string;
    master_label: string;
    active_version_id: string;
    org_id: string;
  };
}

interface GroupedObjectData {
  [objectName: string]: {
    [fieldName: string]: {
      fieldName: string;
      flows: {
        flowId: string;
        flowName: string;
        sfFlowId: string;
        orgId: string;
        operations: string[];
      }[];
    };
  };
}

export default function ObjectsPage() {
  const { user, signOut } = useAuth();
  const { orgs, isLoading: isLoadingOrgs } = useSalesforce();
  const [searchParams] = useSearchParams();
  const { orgId: urlOrgId } = useParams<{ orgId: string }>();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [objectsData, setObjectsData] = useState<SalesforceObject[]>([]);
  const [groupedData, setGroupedData] = useState<GroupedObjectData>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uniqueObjects, setUniqueObjects] = useState<string[]>([]);
  const [uniqueFields, setUniqueFields] = useState<string[]>([]);
  const [selectedObject, setSelectedObject] = useState<string>('');
  const [selectedField, setSelectedField] = useState<string>('');
  const [showModifyingFlowsOnly, setShowModifyingFlowsOnly] = useState(false);
  
  const navigate = useNavigate();
  
  // Define which operations modify data
  const modifyingOperations = ['recordCreates', 'recordUpdates', 'recordDeletes', 'create', 'update', 'delete'];
  
  // Set the selected org from URL params or default to first org
  useEffect(() => {
    // First check URL parameter
    if (urlOrgId) {
      setSelectedOrgId(urlOrgId);
    }
    // Fall back to search params for backward compatibility
    else if (searchParams.get('orgId')) {
      const orgId = searchParams.get('orgId')!;
      // Redirect to new format
      navigate(`/${orgId}/fields`, { replace: true });
    }
    // If no orgId found but orgs exist, redirect to first org
    else if (orgs.length > 0 && !selectedOrgId) {
      navigate(`/${orgs[0].id}/fields`, { replace: true });
    }
  }, [urlOrgId, searchParams, orgs, selectedOrgId, navigate]);
  
  // Fetch Salesforce objects data when the selected org changes
  useEffect(() => {
    const fetchObjectsData = async () => {
      if (!selectedOrgId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Get all flows for this org to ensure we have the relationship data
        const { data: flows, error: flowsError } = await supabase
          .from('sf_flow_definitions')
          .select('id, master_label, active_version_id, org_id')
          .eq('org_id', selectedOrgId);
        
        if (flowsError) throw flowsError;
        
        // Get the flow IDs for this org
        const flowIds = flows.map(flow => flow.id);
        
        if (flowIds.length === 0) {
          setObjectsData([]);
          setGroupedData({});
          setIsLoading(false);
          return;
        }
        
        // Fetch objects affected data for these flows
        const { data, error } = await supabase
          .from('sf_objects')
          .select('*')
          .in('flow_id', flowIds);
        
        if (error) throw error;
        
        // Combine with flow data
        const enrichedData = data.map((item: SalesforceObject) => {
          const flow = flows.find(f => f.id === item.flow_id);
          return {
            ...item,
            flow
          };
        });
        
        setObjectsData(enrichedData);
        
        // Extract unique objects, fields, and flows for filters
        const objects = [...new Set(enrichedData.map(item => item.object_name))].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        setUniqueObjects(objects);
        
        const fields = [...new Set(enrichedData.map(item => item.field_name))].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        setUniqueFields(fields);
        
        
        // Group the data by object and field
        const grouped: GroupedObjectData = {};
        
        enrichedData.forEach((item: SalesforceObject) => {
          const { object_name, field_name, flow_id, relationship_type } = item;
          const flow = flows.find(f => f.id === flow_id);
          
          if (!flow) return;
          
          // Initialize object if it doesn't exist
          if (!grouped[object_name]) {
            grouped[object_name] = {};
          }
          
          // Initialize field if it doesn't exist
          if (!grouped[object_name][field_name]) {
            grouped[object_name][field_name] = {
              fieldName: field_name,
              flows: []
            };
          }
          
          // Check if flow already exists for this field
          const existingFlowIndex = grouped[object_name][field_name].flows.findIndex(
            f => f.flowId === flow_id
          );
          
          if (existingFlowIndex >= 0) {
            // Add operation to existing flow if it's not already there
            if (!grouped[object_name][field_name].flows[existingFlowIndex].operations.includes(relationship_type)) {
              grouped[object_name][field_name].flows[existingFlowIndex].operations.push(relationship_type);
            }
          } else {
            // Add new flow entry
            grouped[object_name][field_name].flows.push({
              flowId: flow.id,
              flowName: flow.master_label,
              sfFlowId: flow.active_version_id,
              orgId: flow.org_id,
              operations: [relationship_type]
            });
          }
        });
        
        setGroupedData(grouped);
      } catch (err) {
        console.error('Error fetching objects data:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };
    
    if (selectedOrgId && !isLoadingOrgs) {
      fetchObjectsData();
    }
  }, [selectedOrgId, isLoadingOrgs]);
  
  
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  
  // Get the selected org from the orgs array
  const selectedOrg = orgs.find(org => org.id === selectedOrgId);
  
  // Apply filtering to the grouped data
  const filteredGroupedData = Object.keys(groupedData).reduce((result, objectName) => {
    // Apply object filter
    if (selectedObject && objectName !== selectedObject) {
      return result;
    }
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    const objectFields = groupedData[objectName];
    
    // Filter fields based on selected field filter and search term
    const filteredFields = Object.keys(objectFields).reduce((fieldResult, fieldName) => {
      // Apply field filter
      if (selectedField && fieldName !== selectedField) {
        return fieldResult;
      }
      
      // Check if field or its flows match search term
      const fieldMatchesSearch = !searchTerm || 
        objectName.toLowerCase().includes(lowerSearchTerm) ||
        fieldName.toLowerCase().includes(lowerSearchTerm) ||
        objectFields[fieldName].flows.some(flow => 
          flow.flowName.toLowerCase().includes(lowerSearchTerm)
        );
      
      if (fieldMatchesSearch) {
        let flowsToInclude = objectFields[fieldName].flows;
        
        // Apply modifying operations filter
        if (showModifyingFlowsOnly) {
          flowsToInclude = flowsToInclude.filter(flow =>
            flow.operations.some(op => 
              modifyingOperations.some(modOp => 
                op.toLowerCase().includes(modOp.toLowerCase())
              )
            )
          );
        }
        
        // If searching, only include flows that match
        if (searchTerm && !objectName.toLowerCase().includes(lowerSearchTerm) && !fieldName.toLowerCase().includes(lowerSearchTerm)) {
          flowsToInclude = flowsToInclude.filter(flow => 
            flow.flowName.toLowerCase().includes(lowerSearchTerm)
          );
        }
        
        if (flowsToInclude.length > 0) {
          fieldResult[fieldName] = {
            fieldName: objectFields[fieldName].fieldName,
            flows: flowsToInclude
          };
        }
      }
      
      return fieldResult;
    }, {} as any);
    
    // Only include object if it has any fields after filtering
    if (Object.keys(filteredFields).length > 0) {
      result[objectName] = filteredFields;
    }
    
    return result;
  }, {} as GroupedObjectData);

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  
  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar onSignOut={handleSignOut} />
      
      <div className="py-10">
        <header>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <h1 className="text-3xl font-bold leading-tight text-gray-900">Field Impact</h1>
              <OrgSwitcher currentOrgId={selectedOrgId} />
            </div>
          </div>
        </header>
        
        <main>
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            {/* Field Impact Analysis Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Field Impact Analysis</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>This page shows which flows reference specific fields in your Salesforce objects. Use the "Show only fields modified by flows" filter to identify:</p>
                    <ul className="list-disc list-inside mt-1">
                      <li>Which flows would be impacted if a field was deprecated</li>
                      <li>Which flows are creating, updating, or deleting data in specific fields</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            {selectedOrgId && !isLoadingOrgs && orgs.length > 0 && (
              <div className="bg-white shadow-md px-4 py-5 sm:rounded-lg sm:p-6 mb-6 mt-6 border border-gray-200">
                {/* Filter Bar */}
                <div>
                  <h2 className="text-lg leading-6 font-medium text-gray-900 mb-4">Filter Options</h2>
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-3">
                    <div>
                      <label htmlFor="object-filter" className="block text-sm font-medium text-gray-700 bg-transparent">
                        Objects
                      </label>
                      <div className="mt-1">
                        <select
                          id="object-filter"
                          name="object-filter"
                          value={selectedObject}
                          onChange={(e) => setSelectedObject(e.target.value)}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md py-2 bg-white text-gray-900"
                        >
                          <option value="">All Objects ({uniqueObjects.length})</option>
                          {uniqueObjects.map(obj => (
                            <option key={obj} value={obj}>{cleanObjectName(obj)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="field-filter" className="block text-sm font-medium text-gray-700 bg-transparent">
                        Fields
                      </label>
                      <div className="mt-1">
                        <select
                          id="field-filter"
                          name="field-filter"
                          value={selectedField}
                          onChange={(e) => setSelectedField(e.target.value)}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md py-2 bg-white text-gray-900"
                        >
                          <option value="">All Fields ({uniqueFields.length})</option>
                          {uniqueFields.map(field => (
                            <option key={field} value={field}>{field.replace(/__c$/, '').replace(/_/g, ' ')}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="search" className="block text-sm font-medium text-gray-700 bg-transparent">
                        Search
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="search"
                          id="search"
                          value={searchTerm}
                          onChange={handleSearchChange}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md py-2 pl-4 bg-white text-gray-900 placeholder-gray-500"
                          placeholder="Search objects, fields, or flows..."
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Modifying Operations Filter */}
                  <div className="mt-4 border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <input
                          id="show-modifying-flows"
                          type="checkbox"
                          checked={showModifyingFlowsOnly}
                          onChange={(e) => setShowModifyingFlowsOnly(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="show-modifying-flows" className="ml-2 block text-sm text-gray-900">
                          Show only fields modified by flows
                        </label>
                      </div>
                      <div className="text-xs text-gray-500">
                        Shows fields used in Create, Update, or Delete operations
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="px-4 py-5 sm:px-0">
              {isLoadingOrgs ? (
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : orgs.length === 0 ? (
                <div className="bg-white shadow overflow-hidden sm:rounded-md p-6 text-center">
                  <p className="text-gray-500">You haven't connected any Salesforce organizations yet.</p>
                  <div className="mt-5">
                    <button
                      onClick={() => navigate('/orgs')}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      + Add Organization
                    </button>
                  </div>
                </div>
              ) : !selectedOrgId ? (
                <div className="bg-white shadow overflow-hidden sm:rounded-md p-6 text-center">
                  <p className="text-gray-500">Please select an organization to view objects and fields.</p>
                </div>
              ) : isLoading ? (
                <div className="flex justify-center p-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                  <p className="ml-3 text-lg text-gray-600">Loading objects data...</p>
                </div>
              ) : error ? (
                <div className="bg-red-50 p-4 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error loading objects data</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{error.message}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : Object.keys(filteredGroupedData).length === 0 ? (
                <div className="bg-white shadow overflow-hidden sm:rounded-md p-6 text-center">
                  <p className="text-gray-500">
                    {searchTerm ? `No objects found matching "${searchTerm}"` : "No object data found for this organization."}
                  </p>
                  {searchTerm && (
                    <div className="mt-5">
                      <button
                        onClick={() => setSearchTerm('')}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Clear Search
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Results summary */}
                  <div className="bg-white px-4 py-3 sm:rounded-lg border border-gray-200 flex justify-between items-center">
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">{Object.keys(filteredGroupedData).length}</span> Salesforce object{Object.keys(filteredGroupedData).length !== 1 ? 's' : ''} found
                    </span>
                    {searchTerm && (
                      <span className="text-sm text-gray-500">
                        Filtering by: "{searchTerm}"
                      </span>
                    )}
                  </div>
                  
                  {/* Table View with three columns: Objects, Fields, Flows */}
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 fields-table">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Object
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Field
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Flows
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {Object.keys(filteredGroupedData).sort().map((objectName, objectIndex) => {
                            // Get all fields for this object
                            const fieldNames = Object.keys(filteredGroupedData[objectName]).sort();
                            
                            // Determine background color for this object's rows
                            const bgColor = objectIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                            
                            return fieldNames.map((fieldName, fieldIndex) => {
                              // Get all flows for this field
                              const fieldFlows = filteredGroupedData[objectName][fieldName].flows.sort((a, b) => a.flowName.localeCompare(b.flowName));
                              
                              return (
                                <tr key={`${objectName}-${fieldName}`} className={`${bgColor} hover:bg-gray-100`}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm align-top">
                                    {fieldIndex === 0 ? (
                                      <div className="font-medium text-gray-900">{cleanObjectName(objectName)}</div>
                                    ) : (
                                      <div></div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 align-top">
                                    {fieldName.replace(/__c$/, '').replace(/_/g, ' ')}
                                  </td>
                                  <td className="px-6 py-4 text-sm align-top">
                                    <div className="space-y-2">
                                      {fieldFlows.map((flow, flowIndex) => {
                                        const hasModifyingOps = flow.operations.some(op => 
                                          modifyingOperations.some(modOp => 
                                            op.toLowerCase().includes(modOp.toLowerCase())
                                          )
                                        );
                                        
                                        return (
                                          <div key={`${fieldName}-${flow.flowId}`} className="flex items-start gap-2">
                                            <div className="flex-grow">
                                              <a 
                                                href={`/${flow.orgId}/flow/${flow.flowId}`}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  navigate(`/${flow.orgId}/flow/${flow.flowId}`);
                                                }}
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                              >
                                                {flow.flowName}
                                              </a>
                                            </div>
                                            <div className="flex gap-1 flex-shrink-0">
                                              {flow.operations.map((op, opIndex) => {
                                                const isModifying = modifyingOperations.some(modOp => 
                                                  op.toLowerCase().includes(modOp.toLowerCase())
                                                );
                                                return (
                                                  <span 
                                                    key={opIndex}
                                                    className={`text-xs px-1.5 py-0.5 rounded ${
                                                      isModifying 
                                                        ? 'bg-red-100 text-red-700' 
                                                        : 'bg-gray-100 text-gray-600'
                                                    }`}
                                                    title={op}
                                                  >
                                                    {op.replace('record', '').replace('s', '')}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </td>
                                </tr>
                              );
                            });
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
      
      {/* Chatbot */}
      <Chatbot />
    </div>
  );
}
